import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import axios from 'axios';
import moment from 'moment';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Fade,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Container,
  Dialog
} from '@mui/material';

import { styled } from '@mui/material/styles';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import AiSprintRetro from './AiSprintRetro'; // Add this line - adjust path as needed
// Styled Components with enhanced styling
const MainContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '24px',

  [theme.breakpoints.down('md')]: {
    padding: '16px',
  },

  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },

  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const HeaderCard = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  textAlign: 'left',
  paddingBottom: theme.spacing(2),
  borderBottom: '1px solid var(--artemy-border)',
}));

const PodCard = styled(Card)(({ theme }) => ({
  background: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  borderRadius: '14px',
  padding: theme.spacing(3),
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
  position: 'relative',
  '&:hover': {
    borderColor: 'var(--artemy-border-bright)',
  },
  transition: 'border-color 0.2s',
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: 'var(--artemy-text-primary)',
  fontFamily: 'var(--artemy-font-display)',
}));

const TableSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const SprintChip = styled(Chip)(({ theme }) => ({
  borderRadius: '6px',
  fontWeight: 600,
  backgroundColor: 'rgba(61, 127, 255, 0.1)',
  color: 'var(--artemy-accent)',
  border: '1px solid rgba(61, 127, 255, 0.2)',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
  gap: theme.spacing(2),
  background: 'var(--artemy-bg-base)',
  color: 'var(--artemy-text-secondary)',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': {
      borderColor: 'var(--artemy-border)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--artemy-border-bright)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--artemy-accent)',
    },
    '& .MuiSelect-icon': {
      color: 'var(--artemy-text-secondary)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--artemy-text-secondary)',
    '&.Mui-focused': {
      color: 'var(--artemy-accent)',
    },
  },
  '& .MuiMenuItem-root': {
    background: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': {
      borderColor: 'var(--artemy-border)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--artemy-border-bright)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--artemy-accent)',
    },
    '& textarea': {
      color: 'var(--artemy-text-primary)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--artemy-text-secondary)',
    '&.Mui-focused': {
      color: 'var(--artemy-accent)',
    },
  },
  '& .MuiInputBase-input': {
    color: 'var(--artemy-text-primary)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontFamily: 'var(--artemy-font-body)',
  backgroundColor: 'var(--artemy-accent)',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#3570e5',
  },
}));

const PodRetroManager = ({ collapsed }) => {
  const token = useAuthToken();
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector(state => state.auth);
  const [showAiSprintRetro, setShowAiSprintRetro] = useState(false);
  // State declarations
  const [loading, setLoading] = useState(true);
  const [podDetails, setPodDetails] = useState([]);
  const [selectedPodId, setSelectedPodId] = useState('');
  const [retrolisting, setRetrolisting] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState('');
  const [sprintData, setSprintData] = useState([]);
  const [currentPageretro, setCurrentPageretro] = useState(1);
  const [teamFilter, setTeamFilter] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFormSection, setShowFormSection] = useState(true);
  const itemsPerPageretro = 5;

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        // Fetch pod details
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_GETCLIENTPOD,
          { clientID: user.data[0].emp_id },
          { headers }
        );

        if (response.data?.data) {
          setPodDetails(response.data.data.podDetails || []);
        }

        // Fetch sprint data
        const sprintResponse = await axios.post(
          process.env.REACT_APP_CLIENT_SPRINTFILTERMANAGER,
          {
            manager_id: user.data[0].emp_id,
            org_code: user.data[0].client_code,
          },
          { headers }
        );

        if (sprintResponse.data?.body) {
          const parsedData = JSON.parse(sprintResponse.data.body);
          setSprintData(parsedData.sprint_filter || []); // Changed from .sprints to .sprint_filter
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [token, user]);

  // Fetch retro listing when pod is selected
  useEffect(() => {
    if (selectedPodId) {
      fetchRetroListing(selectedPodId);
    }
  }, [selectedPodId]);

  const getRetroFields = (retroString) => {
    try {
      return typeof retroString === 'string' ? JSON.parse(retroString) : retroString;
    } catch (error) {
      console.error('Error parsing retro:', error);
      return {};
    }
  };

  const fetchRetroListing = async (podId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_PODRETROLIST,
        { pods_id: podId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data?.data) {
        const processedData = response.data.data.map(item => ({
          ...item,
          retroFields: getRetroFields(item.retro)
        }));
        setRetrolisting(processedData);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching retro listing:', error);
      setLoading(false);
    }
  };

  const handleSubmitpodretro = async (e) => {
    e.preventDefault();
    setLoading(true);

    const retroFeedback = {
      start_doing: e.target.start_doing.value,
      stop_doing: e.target.stop_doing.value,
      keep_doing: e.target.keep_doing.value,
    };

    const formData = {
      pod_feedback: e.target.podRetro.value,
      retro: retroFeedback,
      sprint: selectedSprint,
      status: "Completed",
      submitted_by: user.data[0].emp_firstname || null,
      submitted_by_id: user.data[0].emp_id,
      pods_id: selectedPodId
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_TALENT_PODRETROS,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 200) {
        // Show success message
        alert("Posted Successfully.");
        fetchRetroListing(selectedPodId);
        e.target.reset();
        setSelectedSprint('');
      } else {
        alert(response.data.data || "Error submitting retro");
      }
      setLoading(false);
    } catch (error) {
      console.error('Error submitting retro:', error);
      alert("Error submitting retro. Please try again.");
      setLoading(false);
    }
  };

  const sortedAndFilteredRetros = useMemo(() => {
    let filtered = [...retrolisting];

    if (teamFilter) {
      filtered = filtered.filter(item => item.submitted_by === teamFilter);
    }

    return filtered.sort((a, b) => {
      const dateA = moment(a.createdAt);
      const dateB = moment(b.createdAt);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [retrolisting, teamFilter, sortDirection]);

  const startIndex = (currentPageretro - 1) * itemsPerPageretro;
  const endIndex = Math.min(startIndex + itemsPerPageretro, sortedAndFilteredRetros.length);
  const currentRetros = sortedAndFilteredRetros.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedAndFilteredRetros.length / itemsPerPageretro);

  // Get unique team members for filter
  const uniqueMembers = useMemo(() => {
    return Array.from(new Set(retrolisting.map(item => item.submitted_by)))
      .filter(member => member); // Filter out null/undefined
  }, [retrolisting]);

  const toggleFormSection = () => {
    setShowFormSection(!showFormSection);
  };

  if (loading) {
    return (
      <Fade in={true} timeout={800}>
        <LoadingContainer>
          <CircularProgress size={60} sx={{ color: '#3182CE' }} />
          <Typography variant="body1" color="textSecondary">
            {intl.formatMessage({ id: 'loading' }) || 'Loading...'}
          </Typography>
        </LoadingContainer>
      </Fade>
    );
  }

  return (
    <MainContainer>
      <HeaderCard>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight={700}
          sx={{
            color: 'var(--artemy-text-primary)',
            fontFamily: 'var(--artemy-font-display)',
            letterSpacing: '-0.4px',
            marginBottom: theme.spacing(0.5)
          }}
        >
          {intl.formatMessage({ id: 'podRetro' }) || 'Pod Retrospective Manager'}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'var(--artemy-text-secondary)' }}>
          View and post retrospective feedback for your pod
        </Typography>
      </HeaderCard>

      <Box mb={4} sx={{ maxWidth: '400px', width: '100%', mx: 'auto' }}>
        <StyledFormControl fullWidth variant="outlined" size="medium">
          <InputLabel>{intl.formatMessage({ id: 'selectPod' }) || 'Select Pod'}</InputLabel>
          <Select
            value={selectedPodId}
            onChange={(e) => setSelectedPodId(e.target.value)}
            label={intl.formatMessage({ id: 'selectPod' }) || 'Select Pod'}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {podDetails.map((pod) => (
              <MenuItem key={pod.id} value={pod.id}>
                {pod.podName}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>
      </Box>

      {selectedPodId && (
        <PodCard>
          {showFormSection && (
            <>
              <FormSection>
                <FormSectionTitle variant="h6">
                  <AddIcon fontSize="small" />
                  {intl.formatMessage({ id: 'newRetro' }) || 'New Retrospective'}
                </FormSectionTitle>

                <form onSubmit={handleSubmitpodretro}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <StyledTextField
                        required
                        fullWidth
                        multiline
                        rows={3}
                        name="podRetro"
                        label={intl.formatMessage({ id: 'overallFeedback' }) || 'Overall Pod Feedback'}
                        variant="outlined"
                        placeholder="Share your thoughts about the pod's performance during this sprint..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <StyledFormControl fullWidth required variant="outlined">
                        <InputLabel>{intl.formatMessage({ id: 'sprint' }) || 'Sprint'}</InputLabel>
                        <Select
                          value={selectedSprint}
                          onChange={(e) => setSelectedSprint(e.target.value)}
                          label={intl.formatMessage({ id: 'sprint' }) || 'Sprint'}
                        >
                          {sprintData.map((sprint, index) => (
                            <MenuItem key={index} value={sprint.sprint_name}>
                              {sprint.sprint_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </StyledFormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {intl.formatMessage({ id: 'retroMatrix' }) || 'Retrospective Matrix'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        name="start_doing"
                        label={intl.formatMessage({ id: 'startDoing' }) || 'Start Doing'}
                        variant="outlined"
                        placeholder="What should the team start doing..."
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        name="stop_doing"
                        label={intl.formatMessage({ id: 'stopDoing' }) || 'Stop Doing'}
                        variant="outlined"
                        placeholder="What should the team stop doing..."
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        name="keep_doing"
                        label={intl.formatMessage({ id: 'keepDoing' }) || 'Keep Doing'}
                        variant="outlined"
                        placeholder="What should the team continue doing..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <StyledButton
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={<CheckCircleIcon />}
                      >
                        {intl.formatMessage({ id: 'submitRetro' }) || 'Submit Retrospective'}
                      </StyledButton>
                    </Grid>
                  </Grid>
                </form>
              </FormSection>

              <Divider />
            </>
          )}

          <TableSection>
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <HistoryIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  {intl.formatMessage({ id: 'retroHistory' }) || 'Retrospective History'}
                </Typography>
              </Box>

              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={toggleFormSection}
                  size="small"
                >
                  {showFormSection
                    ? (intl.formatMessage({ id: 'hideForm' }) || 'Hide Form')
                    : (intl.formatMessage({ id: 'showForm' }) || 'Show Form')}
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowAiSprintRetro(true)}
                  size="small"
                >
                  {intl.formatMessage({ id: 'viewSprintAiUpdate' }) || 'View Sprint AI Update'}
                </Button>

                <StyledFormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>
                    {intl.formatMessage({ id: 'filterByMember' }) || 'Filter by Member'}
                  </InputLabel>
                  <Select
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                    label={intl.formatMessage({ id: 'filterByMember' }) || 'Filter by Member'}
                  >
                    <MenuItem value="">
                      {intl.formatMessage({ id: 'allMembers' }) || 'All Members'}
                    </MenuItem>
                    {uniqueMembers.map((member, index) => (
                      <MenuItem key={index} value={member}>{member}</MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Box>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <div className="artemy-card" style={{ padding: '0', overflow: 'hidden' }}>
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr>
                        <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: 'teamMember' }) || 'Member'}</th>
                        <th
                          onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                          style={{ cursor: 'pointer', backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {intl.formatMessage({ id: 'date' }) || 'Date'}
                            <SortIcon fontSize="small" color="action" />
                          </Box>
                        </th>
                        <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: 'sprint' }) || 'Sprint'}</th>
                        <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: 'podFeedback' }) || 'Feedback'}</th>
                        <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: 'startDoing' }) || 'Start Doing'}</th>
                        <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: 'stopDoing' }) || 'Stop Doing'}</th>
                        <th style={{ backgroundColor: 'var(--artemy-bg-base)', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--artemy-border)', padding: '16px', position: 'sticky', top: 0, zIndex: 1 }}>{intl.formatMessage({ id: 'keepDoing' }) || 'Keep Doing'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRetros.length > 0 ? (
                        currentRetros.map((retro, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid var(--artemy-border)', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--artemy-bg-elevated)' } }}>
                            <td style={{ padding: '16px', color: 'var(--artemy-text-primary)' }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: `hsl(${(retro.submitted_by?.charCodeAt(0) || 0) * 10}, 70%, 60%)`,
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {retro.submitted_by?.charAt(0) || '?'}
                                </Avatar>
                                {retro.submitted_by}
                              </Box>
                            </td>
                            <td style={{ padding: '16px', color: 'var(--artemy-text-secondary)' }}>
                              {moment(retro.createdAt).format("MMM DD, YYYY")}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <SprintChip size="small" label={retro.sprint} />
                            </td>
                            <td style={{ padding: '16px', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--artemy-text-primary)' }}>
                              <Tooltip title={retro.pod_feedback || ''}>
                                <Typography variant="body2">
                                  {retro.pod_feedback}
                                </Typography>
                              </Tooltip>
                            </td>
                            <td style={{ padding: '16px', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--artemy-text-primary)' }}>
                              <Tooltip title={retro.retroFields.start_doing || ''}>
                                <Typography variant="body2">
                                  {retro.retroFields.start_doing}
                                </Typography>
                              </Tooltip>
                            </td>
                            <td style={{ padding: '16px', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--artemy-text-primary)' }}>
                              <Tooltip title={retro.retroFields.stop_doing || ''}>
                                <Typography variant="body2">
                                  {retro.retroFields.stop_doing}
                                </Typography>
                              </Tooltip>
                            </td>
                            <td style={{ padding: '16px', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--artemy-text-primary)' }}>
                              <Tooltip title={retro.retroFields.keep_doing || ''}>
                                <Typography variant="body2">
                                  {retro.retroFields.keep_doing}
                                </Typography>
                              </Tooltip>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} style={{ textAlign: 'center', padding: '32px' }}>
                            <Typography variant="body1" color="textSecondary">
                              {intl.formatMessage({ id: 'noRetrosFound' }) || 'No retrospectives found'}
                            </Typography>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 0 && (
                  <Box p={3} display="flex" justifyContent="center" alignItems="center">
                    <Box display="flex" gap={1} alignItems="center">
                      <IconButton
                        onClick={() => setCurrentPageretro(prev => Math.max(1, prev - 1))}
                        disabled={currentPageretro === 1}
                        size="small"
                        sx={{
                          borderRadius: '4px',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <ArrowBackIosNewIcon fontSize="small" />
                      </IconButton>

                      <Box sx={{ minWidth: '120px', textAlign: 'center' }}>
                        <Typography variant="body2">
                          {`${intl.formatMessage({ id: 'page' }) || 'Page'} ${currentPageretro} ${intl.formatMessage({ id: 'of' }) || 'of'} ${totalPages}`}
                        </Typography>
                      </Box>

                      <IconButton
                        onClick={() => setCurrentPageretro(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPageretro === totalPages}
                        size="small"
                        sx={{
                          borderRadius: '4px',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </div>
            </Paper>
          </TableSection>
        </PodCard>
      )}

      {!selectedPodId && (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: '14px',
            background: 'var(--artemy-bg-card)',
            border: '1px solid var(--artemy-border)',
          }}
        >
          <Typography sx={{ fontSize: '13px', color: 'var(--artemy-text-secondary)' }}>
            {intl.formatMessage({ id: 'Select a POD above' }) || 'Use the dropdown above to select a pod and manage its retrospectives'}
          </Typography>
        </Box>
      )}
      {/* AI Sprint Retro Dialog */}
      <Dialog
        open={showAiSprintRetro}
        onClose={() => setShowAiSprintRetro(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', maxHeight: '900px' }
        }}
      >
        <AiSprintRetro
          isModal={true}
          onClose={() => setShowAiSprintRetro(false)}
          selectedPodId={selectedPodId}
          selectedSprint={selectedSprint}
        />
      </Dialog>
    </MainContainer>
  );
};

export default PodRetroManager;